import { useEffect, useCallback, useState } from 'react'
import { createPortal } from 'react-dom'
import { clsx } from 'clsx'
import styles from './SessionTimeoutWarning.module.css'

interface SessionTimeoutWarningProps {
  isOpen: boolean
  secondsRemaining: number
  onContinue: () => void
  onExit: () => void
  mode?: 'self' | 'operator'
}

/**
 * Formatta i secondi in formato mm:ss
 */
function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

/**
 * Modal di avviso timeout sessione GDPR
 * Mostra un countdown e permette all'utente di rinnovare o terminare la sessione
 */
export function SessionTimeoutWarning({
  isOpen,
  secondsRemaining,
  onContinue,
  onExit,
  mode = 'self'
}: SessionTimeoutWarningProps) {
  const [isLoading, setIsLoading] = useState(false)
  
  // Handle continue click
  const handleContinue = useCallback(async () => {
    setIsLoading(true)
    try {
      await onContinue()
    } finally {
      setIsLoading(false)
    }
  }, [onContinue])
  
  // Handle escape key
  const handleEscape = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleContinue()
    }
  }, [handleContinue])
  
  // Handle body scroll lock and escape key
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
      document.addEventListener('keydown', handleEscape)
    }
    
    return () => {
      document.body.style.overflow = ''
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, handleEscape])
  
  // Don't render if not open
  if (!isOpen) return null
  
  const isUrgent = secondsRemaining <= 30
  
  const modalContent = (
    <div className={styles.overlay}>
      <div
        className={clsx(styles.modal, isUrgent && styles.urgent)}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="timeout-title"
        aria-describedby="timeout-description"
      >
        {/* Icon */}
        <div className={styles.iconWrapper}>
          <i className={clsx('bi', isUrgent ? 'bi-exclamation-triangle-fill' : 'bi-clock-history')} />
        </div>
        
        {/* Title */}
        <h2 id="timeout-title" className={styles.title}>
          {isUrgent ? 'Sessione in scadenza!' : 'Sei ancora presente?'}
        </h2>
        
        {/* Description */}
        <p id="timeout-description" className={styles.description}>
          {mode === 'self' ? (
            <>
              Per la tua sicurezza e per rispettare la normativa GDPR sulla protezione dei dati,
              la sessione scade automaticamente dopo un periodo di inattivita.
            </>
          ) : (
            <>
              La sessione operatore sta per scadere. 
              Clicca "Continua" per mantenere attiva la connessione.
            </>
          )}
        </p>
        
        {/* Countdown */}
        <div className={clsx(styles.countdown, isUrgent && styles.countdownUrgent)}>
          <span className={styles.countdownLabel}>Tempo rimanente:</span>
          <span className={styles.countdownValue}>{formatTime(secondsRemaining)}</span>
        </div>
        
        {/* Warning message for GDPR */}
        {mode === 'self' && (
          <p className={styles.gdprNote}>
            <i className="bi bi-shield-lock" />
            Alla scadenza, i tuoi dati saranno eliminati in automatico per proteggere la tua privacy.
          </p>
        )}
        
        {/* Actions */}
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.continueButton}
            onClick={handleContinue}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className={styles.spinner} />
                Rinnovo in corso...
              </>
            ) : (
              <>
                <i className="bi bi-arrow-clockwise" />
                Continua la sessione
              </>
            )}
          </button>
          
          <button
            type="button"
            className={styles.exitButton}
            onClick={onExit}
            disabled={isLoading}
          >
            <i className="bi bi-box-arrow-right" />
            Esci e cancella i dati
          </button>
        </div>
      </div>
    </div>
  )
  
  return createPortal(modalContent, document.body)
}
